/**
 * Book spine: ordered content items (chapters + front/back matter).
 * Only `kind === 'chapter'` participates in chapter numbering.
 */

export const BOOK_CONTENT_KINDS = [
  'chapter',
  'half_title',
  'title_page',
  'copyright',
  'dedication',
  'epigraph',
  'foreword',
  'preface',
  'prologue',
  'toc',
  'section_break',
  'map',
  'epilogue',
  'afterword',
  'acknowledgments',
  'about_author',
  'also_by',
  'appendix',
  'excerpt',
  'bibliography',
  'custom',
] as const;

export type BookContentKind = (typeof BOOK_CONTENT_KINDS)[number];

export const BOOK_CONTENT_KIND_LABELS: Record<BookContentKind, string> = {
  chapter: 'Chapter',
  half_title: 'Half title',
  title_page: 'Title page',
  copyright: 'Copyright',
  dedication: 'Dedication',
  epigraph: 'Epigraph',
  foreword: 'Foreword',
  preface: 'Preface',
  prologue: 'Prologue',
  toc: 'Table of contents',
  section_break: 'Section break',
  map: 'Map',
  epilogue: 'Epilogue',
  afterword: 'Afterword',
  acknowledgments: 'Acknowledgments',
  about_author: 'About the author',
  also_by: 'Also by',
  appendix: 'Appendix',
  excerpt: 'Excerpt',
  bibliography: 'References',
  custom: 'Custom page',
};

/** Default title when inserting a non-chapter item. */
export function defaultTitleForKind(kind: BookContentKind): string {
  if (kind === 'chapter') return 'New Chapter';
  return BOOK_CONTENT_KIND_LABELS[kind];
}

export function isBookContentKind(value: unknown): value is BookContentKind {
  return typeof value === 'string' && (BOOK_CONTENT_KINDS as readonly string[]).includes(value);
}

export type SpineItemForNumbering = {
  id: string;
  kind: BookContentKind | string;
  order: number;
};

/**
 * Assign sequential chapterNumber only to kind=chapter items, in spine order.
 * Non-chapters get null. Returns patches to apply.
 */
export function renumberChapters(
  items: SpineItemForNumbering[]
): Array<{ id: string; chapterNumber: number | null }> {
  const sorted = [...items].sort((a, b) => a.order - b.order);
  let n = 0;
  return sorted.map((item) => {
    if (item.kind === 'chapter') {
      n += 1;
      return { id: item.id, chapterNumber: n };
    }
    return { id: item.id, chapterNumber: null };
  });
}

/**
 * Reindex spine `order` (1..n) and assign `chapterNumber` for kind=chapter.
 * Preserves array order as the spine order.
 */
export function withSpineOrderAndNumbers<T extends { id: string; kind?: BookContentKind | string }>(
  itemsInOrder: T[]
): Array<T & { order: number; chapterNumber: number | null; kind: BookContentKind }> {
  const withOrder = itemsInOrder.map((item, index) => ({
    ...item,
    order: index + 1,
    kind: (isBookContentKind(item.kind) ? item.kind : 'chapter') as BookContentKind,
  }));
  const numbers = renumberChapters(withOrder);
  const byId = new Map(numbers.map((p) => [p.id, p.chapterNumber]));
  return withOrder.map((item) => ({
    ...item,
    chapterNumber: byId.get(item.id) ?? null,
  }));
}
